import { useEffect, useState, useMemo } from 'react';
import {
	ICommentsAverage,
	IPointWithOffset,
} from 'modules/admin-panel/pages/results/models';
import {
	useSetActiveCity,
	useSetActiveCountry,
	useSetActiveOffice,
} from 'hooks/useFilters';

export const useSelectedMapItem = (
	markerList?: ICommentsAverage[] | null,
	withTimeout?: boolean,
) => {
	const [selected, setSelected] = useState<IPointWithOffset>();
	const [hovered, setHovered] = useState<IPointWithOffset>();
	const [lastSelected, setLastSelected] = useState<IPointWithOffset>();
	let timerId: NodeJS.Timeout;
	const onCityChange = useSetActiveCity();
	const onCountryChange = useSetActiveCountry();
	const onOfficeChange = useSetActiveOffice();

	const setAndCashSelected = (item: IPointWithOffset) => {
		setSelected(item);
		if (item) {
			setLastSelected(item);
		}
	};

	useEffect(() => {
		if (!lastSelected || !markerList?.length) return;
		const officeItems = markerList.filter(
			(i) => i.country === lastSelected.country && i.city === lastSelected.city,
		);
		if (!officeItems.length) return;
		if (lastSelected.office && officeItems.length > 1) {
			const selectedOffice = officeItems.find(
				(i) => i.office === lastSelected.office,
			);
			return selectedOffice && setLastSelected(selectedOffice);
		}
		setLastSelected(officeItems[0]);
	}, [markerList, lastSelected]);

	useEffect(() => {
		// reset selected to hide mapMarkerInfo after change
		// zoom level from the Global to the City level (when offices are shown)
		if (markerList?.length && markerList[0].office && !lastSelected?.office) {
			setSelected(undefined);
		}
	}, [markerList, lastSelected]);

	const handleSelected = (marker: ICommentsAverage) => (
		e: google.maps.MouseEvent,
	) => {
		if (marker.city) {
			e.stop();
			onCityChange(marker.city as string, marker.country as string);
		}
		if (marker.office) {
			e.stop();
			onOfficeChange(
				marker.office as string,
				marker.city as string,
				marker.country as string,
			);
		}
		if (withTimeout) {
			clearTimeout(timerId);
		}
	};
	const handleHover = (marker: ICommentsAverage) => (
		e: google.maps.MouseEvent,
	) => {
		if (marker.city) {
			setHovered(marker);
		}
		if (marker.office) {
			setHovered(marker);
		}
		if (withTimeout) {
			clearTimeout(timerId);
		}
	};

	const handleMouseOut = (e: google.maps.MouseEvent) => {
		e.stop();

		if (withTimeout) {
			timerId = setTimeout(() => setSelected(undefined), 3000);
		} else {
			setSelected(undefined);
			setHovered(undefined);
		}
	};

	return {
		selected,
		hovered,
		lastSelected,
		setLastSelected,
		handleSelected,
		handleMouseOut,
		handleHover,
	};
};
